// Advanced filter panel for the Incidents page.
// URL-state-synced via useSearchParams so refresh/share works.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, X, Search } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_VALUES = ["open", "acknowledged", "in_progress", "escalated", "resolved", "closed"];
const CATEGORIES = [
  { v: "quality_lock", l: "Quality Lock" },
  { v: "inbound_transport_delay", l: "Inbound Transport Delay" },
  { v: "line_stoppage", l: "Line Stoppage" },
  { v: "packaging_shortage", l: "Packaging Shortage" },
  { v: "raw_material_shortage", l: "Raw Material Shortage" },
  { v: "customs_border_hold", l: "Customs / Border" },
  { v: "jis_sequence_error", l: "JIS Sequence Error" },
  { v: "warehouse_damage", l: "Warehouse Damage" },
];
const SLA_OPTIONS = [
  { v: "", l: "All" },
  { v: "on_track", l: "On track (<75%)" },
  { v: "warning", l: "Warning (75–99%)" },
  { v: "breach", l: "Breach (100%+)" },
];

// Parse / serialize array params (comma-separated in URL)
const arr = (s) => (s ? s.split(",").filter(Boolean) : []);
const j = (a) => (a && a.length ? a.join(",") : null);

const applyDatePreset = (preset) => {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case "today":
      return { date_from: iso(now), date_to: iso(now) };
    case "7d": {
      const d = new Date(now); d.setDate(d.getDate() - 7); return { date_from: iso(d), date_to: iso(now) };
    }
    case "30d": {
      const d = new Date(now); d.setDate(d.getDate() - 30); return { date_from: iso(d), date_to: iso(now) };
    }
    case "90d": {
      const d = new Date(now); d.setDate(d.getDate() - 90); return { date_from: iso(d), date_to: iso(now) };
    }
    default:
      return { date_from: null, date_to: null };
  }
};

// Returns the active-filter count (used for the badge on the Filters button)
export const activeFilterCount = (params) => {
  let n = 0;
  if (arr(params.get("status")).length) n++;
  if (params.get("category")) n++;
  if (params.get("tenant_id")) n++;
  if (params.get("plant")) n++;
  if (params.get("opened_by")) n++;
  if (params.get("date_from") || params.get("date_to")) n++;
  if (params.get("sla_status")) n++;
  if (params.get("min_eur_loss")) n++;
  if (params.get("has_8d")) n++;
  if (params.get("has_lpa")) n++;
  if (params.get("q")) n++;
  return n;
};

// Hook — exposes filter values as object + setters that write to URL
export const useIncidentFilters = () => {
  const [params, setParams] = useSearchParams();

  const get = (k) => params.get(k) || "";

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === "" || (Array.isArray(v) && !v.length)) {
        next.delete(k);
      } else {
        next.set(k, Array.isArray(v) ? v.join(",") : String(v));
      }
    });
    setParams(next, { replace: true });
  };

  const clearAll = () => {
    const next = new URLSearchParams(params);
    [
      "status", "category", "tenant_id", "plant", "opened_by",
      "date_from", "date_to", "sla_status", "min_eur_loss",
      "has_8d", "has_lpa", "q",
    ].forEach((k) => next.delete(k));
    setParams(next, { replace: true });
  };

  return { params, get, set, clearAll, count: activeFilterCount(params) };
};

// Build query string for backend (without UI-only keys like sev/sla)
export const buildBackendQuery = (params) => {
  const qs = new URLSearchParams();
  const status = arr(params.get("status"));
  status.forEach((s) => qs.append("status", s));
  ["category", "tenant_id", "plant", "opened_by", "date_from", "date_to", "sla_status", "min_eur_loss", "q"].forEach((k) => {
    const v = params.get(k);
    if (v) qs.set(k, v);
  });
  if (params.get("has_8d") && params.get("has_8d") !== "any") qs.set("has_8d", params.get("has_8d"));
  if (params.get("has_lpa") && params.get("has_lpa") !== "any") qs.set("has_lpa", params.get("has_lpa"));
  return qs.toString();
};

export const IncidentFilters = ({ tenants = [], plants = [], totalCount, filteredCount, expanded, onToggle }) => {
  const { params, set, clearAll, count } = useIncidentFilters();
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  // Debounced free-text search
  useEffect(() => {
    const id = setTimeout(() => set({ q: searchInput }), 300);
    return () => clearTimeout(id);
     
  }, [searchInput]);

  const statusActive = arr(params.get("status"));
  const toggleStatus = (s) => {
    const next = statusActive.includes(s) ? statusActive.filter((x) => x !== s) : [...statusActive, s];
    set({ status: next });
  };

  const tags = useMemo(() => {
    const t = [];
    if (statusActive.length) t.push({ label: `Status: ${statusActive.join(", ").toUpperCase()}`, clear: () => set({ status: [] }) });
    const cat = params.get("category");
    if (cat) t.push({ label: `Category: ${CATEGORIES.find((c) => c.v === cat)?.l || cat}`, clear: () => set({ category: null }) });
    const t1 = params.get("tenant_id");
    if (t1) t.push({ label: `Tenant: ${t1}`, clear: () => set({ tenant_id: null }) });
    const plt = params.get("plant");
    if (plt) t.push({ label: `Plant: ${plt}`, clear: () => set({ plant: null }) });
    const ob = params.get("opened_by");
    if (ob) t.push({ label: `Opened by: ${ob}`, clear: () => set({ opened_by: null }) });
    const df = params.get("date_from"); const dt = params.get("date_to");
    if (df || dt) t.push({ label: `Date: ${df || "–"} → ${dt || "–"}`, clear: () => set({ date_from: null, date_to: null }) });
    const slas = params.get("sla_status");
    if (slas) t.push({ label: `SLA: ${SLA_OPTIONS.find((x) => x.v === slas)?.l || slas}`, clear: () => set({ sla_status: null }) });
    const ml = params.get("min_eur_loss");
    if (ml) t.push({ label: `Min loss: €${ml}`, clear: () => set({ min_eur_loss: null }) });
    const h8 = params.get("has_8d");
    if (h8 && h8 !== "any") t.push({ label: `Has 8D: ${h8}`, clear: () => set({ has_8d: null }) });
    const hlp = params.get("has_lpa");
    if (hlp && hlp !== "any") t.push({ label: `Has LPA: ${hlp}`, clear: () => set({ has_lpa: null }) });
    const q = params.get("q");
    if (q) t.push({ label: `Search: "${q}"`, clear: () => { setSearchInput(""); set({ q: null }); } });
    return t;
     
  }, [params]);

  return (
    <div className="mb-4" data-testid="incident-filters">
      {/* Toggle button + result count */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
          style={{
            background: expanded ? "rgba(24,95,165,0.18)" : "transparent",
            border: `1px solid ${expanded ? "#185FA5" : "rgba(255,255,255,0.10)"}`,
            color: expanded ? "#9ec3ec" : "#c5cad8",
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.4px",
            cursor: "pointer",
          }}
          data-testid="filters-toggle-btn"
        >
          <Filter size={12} />
          Filters
          {count > 0 && (
            <span
              className="inline-flex items-center justify-center rounded-full"
              style={{
                minWidth: 18,
                height: 18,
                padding: "0 6px",
                background: "#185FA5",
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
              }}
              data-testid="filters-count-badge"
            >
              {count}
            </span>
          )}
        </button>
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            color: "#9198b0",
            letterSpacing: "0.4px",
          }}
          data-testid="filters-result-count"
        >
          {count > 0
            ? `Showing ${filteredCount} of ${totalCount} incidents`
            : `${totalCount} incidents`}
        </div>
      </div>

      {/* Filter panel */}
      {expanded && (
        <div
          className="rounded-lg p-4 space-y-3"
          style={{
            background: "#0d0f18",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
          data-testid="filters-panel"
        >
          {/* Row 1 — Status pills */}
          <div>
            <RowLabel>Status</RowLabel>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_VALUES.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleStatus(s)}
                  className="px-2.5 py-1 rounded-full"
                  style={{
                    background: statusActive.includes(s) ? "#185FA5" : "transparent",
                    border: `1px solid ${statusActive.includes(s) ? "#185FA5" : "rgba(255,255,255,0.10)"}`,
                    color: statusActive.includes(s) ? "#fff" : "#9aa1b3",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                  data-testid={`filter-status-${s}`}
                >
                  {s.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2 — Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <SelectField
              label="Tenant / Party"
              testid="filter-tenant"
              value={params.get("tenant_id") || ""}
              onChange={(v) => set({ tenant_id: v || null })}
              options={[{ v: "", l: "All" }].concat(tenants.map((t) => ({ v: t.name, l: t.name })))}
            />
            <SelectField
              label="Category"
              testid="filter-category"
              value={params.get("category") || ""}
              onChange={(v) => set({ category: v || null })}
              options={[{ v: "", l: "All" }].concat(CATEGORIES)}
            />
            <SelectField
              label="Plant / Line"
              testid="filter-plant"
              value={params.get("plant") || ""}
              onChange={(v) => set({ plant: v || null })}
              options={[{ v: "", l: "All" }].concat(
                plants.map((p) => ({ v: `${p.plant} · ${p.line}`, l: `${p.plant} · ${p.line}` })),
              )}
            />
            <TextField
              label="Opened by"
              testid="filter-opened-by"
              value={params.get("opened_by") || ""}
              onChange={(v) => set({ opened_by: v || null })}
              placeholder="user name…"
            />
          </div>

          {/* Row 3 — Date range + presets */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto_auto_auto] gap-2 items-end">
            <DateField
              label="From"
              testid="filter-date-from"
              value={params.get("date_from") || ""}
              onChange={(v) => set({ date_from: v || null })}
            />
            <DateField
              label="To"
              testid="filter-date-to"
              value={params.get("date_to") || ""}
              onChange={(v) => set({ date_to: v || null })}
            />
            {[
              { id: "today", l: "Today" }, { id: "7d", l: "7 days" },
              { id: "30d", l: "30 days" }, { id: "90d", l: "90 days" },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => set(applyDatePreset(p.id))}
                className="px-2 py-1.5 rounded transition-colors"
                style={{
                  background: "transparent",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "#c5cad8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.4px",
                  cursor: "pointer",
                  height: 30,
                }}
                data-testid={`filter-preset-${p.id}`}
              >
                {p.l}
              </button>
            ))}
          </div>

          {/* Row 4 — Impact filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <SelectField
              label="SLA Status"
              testid="filter-sla"
              value={params.get("sla_status") || ""}
              onChange={(v) => set({ sla_status: v || null })}
              options={SLA_OPTIONS}
            />
            <NumberField
              label="Min EUR loss"
              testid="filter-min-loss"
              value={params.get("min_eur_loss") || ""}
              onChange={(v) => set({ min_eur_loss: v || null })}
              placeholder="€"
            />
            <SelectField
              label="Has 8D"
              testid="filter-has-8d"
              value={params.get("has_8d") || "any"}
              onChange={(v) => set({ has_8d: v === "any" ? null : v })}
              options={[{ v: "any", l: "Any" }, { v: "true", l: "Yes" }, { v: "false", l: "No" }]}
            />
            <SelectField
              label="Has LPA"
              testid="filter-has-lpa"
              value={params.get("has_lpa") || "any"}
              onChange={(v) => set({ has_lpa: v === "any" ? null : v })}
              options={[{ v: "any", l: "Any" }, { v: "true", l: "Yes" }, { v: "false", l: "No" }]}
            />
          </div>

          {/* Row 5 — Free text */}
          <div>
            <RowLabel>Search</RowLabel>
            <div className="relative">
              <Search
                size={13}
                className="absolute"
                style={{ left: 10, top: 10, color: "#9198b0", pointerEvents: "none" }}
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search in title, description, part numbers, comments…"
                className="w-full rounded-md pl-8 pr-3 py-2"
                style={{
                  background: "#0a0c12",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "#f0f2f8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                }}
                data-testid="filter-search-input"
              />
            </div>
          </div>
        </div>
      )}

      {/* Active filter tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="filter-tags">
          {tags.map((t, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full"
              style={{
                background: "rgba(24,95,165,0.12)",
                border: "1px solid rgba(24,95,165,0.45)",
                color: "#9ec3ec",
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.4px",
              }}
              data-testid={`filter-tag-${i}`}
            >
              {t.label}
              <button
                onClick={t.clear}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#9ec3ec",
                  cursor: "pointer",
                  padding: 0,
                  display: "inline-flex",
                }}
                aria-label="Remove filter"
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <button
            onClick={() => { setSearchInput(""); clearAll(); }}
            className="px-2 py-1 rounded"
            style={{
              background: "transparent",
              border: "1px solid rgba(226,75,74,0.35)",
              color: "#E24B4A",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.4px",
              cursor: "pointer",
            }}
            data-testid="filter-clear-all"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

const RowLabel = ({ children }) => (
  <div
    style={{
      fontFamily: "'DM Mono', monospace",
      fontSize: 9,
      color: "#9198b0",
      letterSpacing: "1.5px",
      textTransform: "uppercase",
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

const baseField = {
  background: "#0a0c12",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "#f0f2f8",
  fontFamily: "'DM Mono', monospace",
  fontSize: 11,
  borderRadius: 4,
  padding: "6px 8px",
  height: 30,
  width: "100%",
  outline: "none",
};

const SelectField = ({ label, options, value, onChange, testid }) => (
  <div>
    <RowLabel>{label}</RowLabel>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={baseField}
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  </div>
);

const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <RowLabel>{label}</RowLabel>
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={baseField}
      data-testid={testid}
    />
  </div>
);

const NumberField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <RowLabel>{label}</RowLabel>
    <input
      type="number"
      min="0"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={baseField}
      data-testid={testid}
    />
  </div>
);

const DateField = ({ label, value, onChange, testid }) => (
  <div>
    <RowLabel>{label}</RowLabel>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={baseField}
      data-testid={testid}
    />
  </div>
);

// Helper hook to load tenants + plants from API (used by Incidents.jsx parent)
export const useFilterDirectories = () => {
  const [tenants, setTenants] = useState([]);
  const [plants, setPlants] = useState([]);
  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/tenants`).catch(() => ({ data: [] })),
      axios.get(`${API}/plants`).catch(() => ({ data: [] })),
    ]).then(([t, p]) => {
      if (!alive) return;
      setTenants(Array.isArray(t.data) ? t.data : []);
      setPlants(Array.isArray(p.data) ? p.data : []);
    });
    return () => { alive = false; };
  }, []);
  return { tenants, plants };
};
